import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Close as CloseIcon, PlayCircle as PlayCircleIcon } from "@mui/icons-material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import VideoPlayer from "src/@core/components/common/VideoPlayer";
import { useTranslation } from "react-i18next";

// ─── Timing ────────────────────────────────────────────────────────────────
const INTRO_HOLD_MS = [2800, 3000, 3200, 2800];
const INTRO_ENTER_MS = 900;
const INTRO_GRID_STAGGER_MS = 70;
const INTRO_GRID_LABEL_AFTER_ICON_MS = Math.round(INTRO_ENTER_MS * 0.42);
const FEATURES_ENTER_MS = 700;

const ugGridCellEnter = keyframes`
  from { opacity: 0; transform: translate3d(0, 12px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

// ─── Sizing & palette ──────────────────────────────────────────────────────
const INTRO_TILE = 56;
const FEATURE_ROW_ICON = 36;
const DETAIL_ICON = 40;
const PURPLE_ACCENT = "#6c00e6";
const YELLOW_ACCENT = "#e6c200";

// Tile descriptors used in the intro icon grid (slide 2).
// Order/colors mirror the SetupDialog steps so the visual language is consistent.
const INTRO_GRID_TILES = [
  {
    key: "email",
    Icon: MailOutlineIcon,
    labelKey: "businessGuideTile_email",
    labelColor: "#D16CFF",
    grad: "linear-gradient(145deg, #F4A6FF, #D16CFF)",
    borderGrad: "linear-gradient(145deg, rgba(244,166,255,0.85), rgba(209,108,255,0.4))",
    shadow: "0 14px 38px rgba(209,108,255,0.28)",
  },
  {
    key: "location",
    Icon: LocationOnIcon,
    labelKey: "businessGuideTile_location",
    labelColor: "#FF5C5C",
    grad: "linear-gradient(145deg, #FF9472, #FF5C5C)",
    borderGrad: "linear-gradient(145deg, rgba(255,148,114,0.85), rgba(255,92,92,0.4))",
    shadow: "0 14px 38px rgba(255,92,92,0.26)",
  },
  {
    key: "team",
    Icon: GroupAddIcon,
    labelKey: "businessGuideTile_team",
    labelColor: "#FFAA3A",
    grad: "linear-gradient(145deg, #FFC97A, #FFAA3A)",
    borderGrad: "linear-gradient(145deg, rgba(255,201,122,0.85), rgba(255,170,58,0.4))",
    shadow: "0 14px 36px rgba(255,170,58,0.24)",
  },
  {
    key: "menu",
    Icon: MenuBookIcon,
    labelKey: "businessGuideTile_menu",
    labelColor: "#FF6F91",
    grad: "linear-gradient(145deg, #FFA7BB, #FF6F91)",
    borderGrad: "linear-gradient(145deg, rgba(255,167,187,0.85), rgba(255,111,145,0.4))",
    shadow: "0 14px 36px rgba(255,111,145,0.22)",
  },
  {
    key: "settings",
    Icon: ChatBubbleOutlineIcon,
    labelKey: "businessGuideTile_settings",
    labelColor: "#478AFF",
    grad: "linear-gradient(145deg, #7CB0FF, #478AFF)",
    borderGrad: "linear-gradient(145deg, rgba(124,176,255,0.85), rgba(71,138,255,0.4))",
    shadow: "0 14px 38px rgba(71,138,255,0.24)",
  },
  {
    key: "business",
    Icon: BusinessCenterIcon,
    labelKey: "businessGuideTile_business",
    labelColor: "#5C6EF0",
    grad: "linear-gradient(145deg, #8EA4FF, #3A54DD)",
    borderGrad: "linear-gradient(145deg, rgba(142,164,255,0.85), rgba(58,84,221,0.4))",
    shadow: "0 14px 36px rgba(92,110,240,0.22)",
  },
  {
    key: "wallet",
    Icon: AccountBalanceWalletIcon,
    labelKey: "businessGuideTile_wallet",
    labelColor: "#34C759",
    grad: "linear-gradient(145deg, #79E89C, #34C759)",
    borderGrad: "linear-gradient(145deg, rgba(121,232,156,0.85), rgba(52,199,89,0.4))",
    shadow: "0 14px 38px rgba(52,199,89,0.24)",
  },
];

// Full list shown on the "setup & earn" slide and the items the user can watch.
// `gradKey` references the matching color from INTRO_GRID_TILES so we don't repeat ourselves.
const BUSINESS_GUIDE_FEATURES = [
  { key: "qr",       Icon: QrCode2Icon,                grad: "linear-gradient(145deg, #B98BFF, #6c00e6)", shadow: "0 14px 38px rgba(108,0,230,0.28)" },
  { key: "email",    Icon: MailOutlineIcon,            grad: INTRO_GRID_TILES[0].grad, shadow: INTRO_GRID_TILES[0].shadow },
  { key: "location", Icon: LocationOnIcon,             grad: INTRO_GRID_TILES[1].grad, shadow: INTRO_GRID_TILES[1].shadow },
  { key: "team",     Icon: GroupAddIcon,               grad: INTRO_GRID_TILES[2].grad, shadow: INTRO_GRID_TILES[2].shadow },
  { key: "menu",     Icon: MenuBookIcon,               grad: INTRO_GRID_TILES[3].grad, shadow: INTRO_GRID_TILES[3].shadow },
  { key: "settings", Icon: ChatBubbleOutlineIcon,      grad: INTRO_GRID_TILES[4].grad, shadow: INTRO_GRID_TILES[4].shadow },
  { key: "verify",   Icon: BusinessCenterIcon,         grad: INTRO_GRID_TILES[5].grad, shadow: INTRO_GRID_TILES[5].shadow },
  { key: "wallet",   Icon: AccountBalanceWalletIcon,   grad: INTRO_GRID_TILES[6].grad, shadow: INTRO_GRID_TILES[6].shadow },
];

/** Per-feature detail popup copy and layout; set `videoUrl` when assets are ready */
const BUSINESS_GUIDE_FEATURE_DETAILS = {
  qr: {
    titleParts: [
      { key: "businessGuideDetail_qr_titleHighlight", highlight: true },
      { key: "businessGuideDetail_qr_titleRest", highlight: false },
    ],
    descKey: "businessGuideDetail_qr_desc",
    bullets: [],
    footerKey: "businessGuideDetail_qr_footer",
    videoUrl: null,
    videoPoster: null,
  },
  email: {
    titleParts: [
      { key: "businessGuideDetail_email_titlePrefix", highlight: false },
      { key: "businessGuideDetail_email_titleHighlight", highlight: true },
    ],
    descKey: "businessGuideDetail_email_desc",
    bullets: [],
    footerKey: "businessGuideDetail_email_footer",
    videoUrl: null,
    videoPoster: null,
  },
  location: {
    titleParts: [
      { key: "businessGuideDetail_location_titlePrefix", highlight: false },
      { key: "businessGuideDetail_location_titleHighlight", highlight: true },
    ],
    descKey: "businessGuideDetail_location_desc",
    bullets: [],
    footerKey: "businessGuideDetail_location_footer",
    videoUrl: null,
    videoPoster: null,
  },
  team: {
    titleParts: [
      { key: "businessGuideDetail_team_titlePrefix", highlight: false },
      { key: "businessGuideDetail_team_titleHighlight", highlight: true },
    ],
    descKey: "businessGuideDetail_team_desc",
    bullets: [],
    footerKey: "businessGuideDetail_team_footer",
    videoUrl: null,
    videoPoster: null,
  },
  menu: {
    titleParts: [
      { key: "businessGuideDetail_menu_titlePrefix", highlight: false },
      { key: "businessGuideDetail_menu_titleHighlight", highlight: true },
    ],
    descKey: "businessGuideDetail_menu_desc",
    bullets: [],
    footerKey: "businessGuideDetail_menu_footer",
    videoUrl: null,
    videoPoster: null,
  },
  settings: {
    titleParts: [
      { key: "businessGuideDetail_settings_titlePrefix", highlight: false },
      { key: "businessGuideDetail_settings_titleHighlight", highlight: true },
    ],
    descKey: "businessGuideDetail_settings_desc",
    bullets: [
      "businessGuideDetail_settings_bullet_1",
      "businessGuideDetail_settings_bullet_2",
      "businessGuideDetail_settings_bullet_3",
      "businessGuideDetail_settings_bullet_4",
      "businessGuideDetail_settings_bullet_5",
      "businessGuideDetail_settings_bullet_6",
      "businessGuideDetail_settings_bullet_7",
      "businessGuideDetail_settings_bullet_8",
    ],
    footerKey: "businessGuideDetail_settings_footer",
    videoUrl: null,
    videoPoster: null,
  },
  verify: {
    titleParts: [
      { key: "businessGuideDetail_verify_titleHighlight", highlight: true },
      { key: "businessGuideDetail_verify_titleRest", highlight: false },
    ],
    descKey: "businessGuideDetail_verify_desc",
    bullets: [],
    footerKey: "businessGuideDetail_verify_footer",
    videoUrl: null,
    videoPoster: null,
  },
  wallet: {
    titleParts: [
      { key: "businessGuideDetail_wallet_titleHighlight", highlight: true },
      { key: "businessGuideDetail_wallet_titleRest", highlight: false },
    ],
    descKey: "businessGuideDetail_wallet_desc",
    bullets: [],
    footerKey: "businessGuideDetail_wallet_footer",
    videoUrl: null,
    videoPoster: null,
  },
};

// ─── Intro grid tile (icon enters first, label same animation after) ───────
function BusinessGuideIntroGridCell({
  Icon,
  labelKey,
  labelColor,
  grad,
  borderGrad,
  shadow,
  size,
  cellIndex,
  t,
}) {
  const iconDelay = cellIndex * INTRO_GRID_STAGGER_MS;
  const labelDelay = cellIndex * INTRO_GRID_STAGGER_MS + INTRO_GRID_LABEL_AFTER_ICON_MS;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.85, width: 76, flexShrink: 0 }}>
      <Box
        sx={{
          borderRadius: "18px",
          padding: "2px",
          background: borderGrad,
          boxShadow: shadow,
          display: "inline-flex",
          animation: `${ugGridCellEnter} ${INTRO_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) both`,
          animationDelay: `${iconDelay}ms`,
        }}
      >
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: grad,
            boxShadow: "inset 0 2px 8px rgba(255,255,255,0.22)",
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: size * 0.55 }} />
        </Box>
      </Box>
      <Typography
        component="span"
        sx={{
          display: "inline-block",
          fontSize: "0.78rem",
          fontWeight: 600,
          color: labelColor,
          textTransform: "lowercase",
          letterSpacing: "0.02em",
          animation: `${ugGridCellEnter} ${INTRO_ENTER_MS}ms cubic-bezier(0.4, 0, 0.2, 1) both`,
          animationDelay: `${labelDelay}ms`,
        }}
      >
        {t(labelKey)}
      </Typography>
    </Box>
  );
}

// ─── Detail popup ──────────────────────────────────────────────────────────
function BusinessGuideFeatureDetailDialog({ featureKey, onClose, t }) {
  const feature = BUSINESS_GUIDE_FEATURES.find((f) => f.key === featureKey);
  const detail = featureKey ? BUSINESS_GUIDE_FEATURE_DETAILS[featureKey] : null;
  const open = Boolean(featureKey && feature && detail);

  if (!feature || !detail) return null;

  const { Icon, grad, shadow } = feature;
  const { titleParts, descKey, bullets, footerKey, videoUrl, videoPoster } = detail;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{
        sx: {
          borderRadius: "22px",
          overflow: "hidden",
          maxHeight: "92vh",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
        },
      }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
    >
      <DialogContent sx={{ p: { xs: 3, sm: 5 }, position: "relative" }}>
        <IconButton
          onClick={onClose}
          aria-label={t("close")}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "#999", zIndex: 1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box sx={{ display: "flex", gap: 1.25, alignItems: "center", pr: 4, mb: 1 }}>
          <Box
            sx={{
              width: DETAIL_ICON,
              height: DETAIL_ICON,
              minWidth: DETAIL_ICON,
              borderRadius: "50%",
              background: grad,
              boxShadow: shadow,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ color: "#fff", fontSize: DETAIL_ICON * 0.56 }} />
          </Box>
          <Typography
            component="div"
            sx={{ fontSize: "1.0625rem", fontWeight: 700, lineHeight: 1.25 }}
          >
            {titleParts.map(({ key, highlight }) => (
              <Box
                key={key}
                component="span"
                sx={{ color: highlight ? PURPLE_ACCENT : "#1a1a1a" }}
              >
                {t(key)}
              </Box>
            ))}
          </Typography>
        </Box>

        <Typography
          sx={{
            color: "#777",
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            mb: bullets.length ? 1.25 : 1.75,
          }}
        >
          {t(descKey)}
        </Typography>

        {bullets.length > 0 && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              columnGap: 1.5,
              rowGap: 0.4,
              mb: 1.75,
            }}
          >
            {bullets.map((bulletKey) => (
              <Box key={bulletKey} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                <Box
                  component="span"
                  sx={{
                    mt: "7px",
                    width: 6,
                    height: 6,
                    minWidth: 6,
                    borderRadius: "50%",
                    background: PURPLE_ACCENT,
                  }}
                />
                <Typography sx={{ color: "#444", fontSize: "0.78rem", lineHeight: 1.45 }}>
                  {t(bulletKey)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            aspectRatio: "16 / 10",
            bgcolor: "#f0ebfa",
            border: `2px solid ${PURPLE_ACCENT}`,
          }}
        >
          {videoUrl ? (
            <VideoPlayer
              videoUrl={videoUrl}
              videoPoster={videoPoster}
              aspectRatio={16 / 10}
            />
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(145deg, #EDE4FA 0%, #D8C4F5 100%)",
              }}
            >
              <PlayCircleIcon sx={{ fontSize: 44, color: "rgba(108,0,230,0.35)" }} aria-hidden />
            </Box>
          )}
        </Box>

        <Typography
          sx={{
            color: "#1976d2",
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            mt: 1.5,
            fontWeight: 500,
          }}
        >
          {t(footerKey)}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main dialog ───────────────────────────────────────────────────────────
const BusinessGuideDialog = ({ open, onClose, businessName }) => {
  const { t } = useTranslation("dashboard");

  // 0: hello, 1: excited, 2: icon grid, 3: let's set you up, 4+: setup & earn list
  const [step, setStep] = useState(0);
  const [detailFeatureKey, setDetailFeatureKey] = useState(null);

  const displayBusinessName = React.useMemo(() => {
    const raw = (businessName || t("businessFallback")).trim();
    if (!raw) return t("businessFallback");
    return raw.toLowerCase();
  }, [businessName, t]);

  useEffect(() => {
    if (!open) return undefined;
    setStep(0);
    setDetailFeatureKey(null);
    const [d0, d1, d2, d3] = INTRO_HOLD_MS;
    const t1 = setTimeout(() => setStep(1), d0);
    const t2 = setTimeout(() => setStep(2), d0 + d1);
    const t3 = setTimeout(() => setStep(3), d0 + d1 + d2);
    const t4 = setTimeout(() => setStep(4), d0 + d1 + d2 + d3);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [open]);

  const handleClose = useCallback(() => {
    setDetailFeatureKey(null);
    onClose?.();
  }, [onClose]);

  const openFeatureDetail = useCallback((key) => {
    if (BUSINESS_GUIDE_FEATURE_DETAILS[key]) setDetailFeatureKey(key);
  }, []);

  const closeFeatureDetail = useCallback(() => setDetailFeatureKey(null), []);

  return (
    <Dialog
      open={open}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={step < 4}
      sx={{ zIndex: 1450 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden", maxHeight: "90vh" } }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(2.5px)" } } }}
    >
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>

        {/* ── Slides 0–3 ── */}
        {step < 4 && (
          <Box sx={{ minHeight: 320, display: "flex", alignItems: "center", justifyContent: "center", px: 3, py: 4 }}>
            <Box
              key={step}
              sx={{
                width: "100%",
                maxWidth: step === 2 ? 420 : 360,
                mx: "auto",
                textAlign: "center",
                "@keyframes bgIntroEnter": {
                  from: { opacity: 0, transform: "translate3d(0,16px,0)" },
                  to:   { opacity: 1, transform: "translate3d(0,0,0)" },
                },
                ...(step !== 2 && {
                  animation: `bgIntroEnter ${INTRO_ENTER_MS}ms cubic-bezier(0.4,0,0.2,1) both`,
                }),
              }}
            >
              {/* Slide 0: hello, <business> */}
              {step === 0 && (
                <Box
                  sx={{
                    width: "min(241px, 85vw)",
                    height: "min(241px, 85vw)",
                    mx: "auto",
                    borderRadius: "50%",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      "linear-gradient(180deg, #FFFFFF 0%, #FCFAFE 42%, #F2E8FA 100%)",
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#1a1a1a",
                      textAlign: "center",
                      px: 2,
                      lineHeight: 1.3,
                    }}
                  >
                    {t("businessGuideHello")}
                    <Box component="div" sx={{ color: YELLOW_ACCENT, fontWeight: 700, mt: 0.25 }}>
                      {displayBusinessName}
                    </Box>
                  </Typography>
                </Box>
              )}

              {/* Slide 1: we're excited to have you on beɘmz business */}
              {step === 1 && (
                <Typography
                  component="div"
                  sx={{ fontSize: "1.375rem", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.35 }}
                >
                  {t("businessGuideExcitedPrefix")}{" "}
                  <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline" }}>
                    <Box component="span" sx={{ color: "#1a1a1a" }}>be</Box>
                    <Box component="span" sx={{ color: PURPLE_ACCENT }}>ɘ</Box>
                    <Box component="span" sx={{ color: "#1a1a1a" }}>mz</Box>
                  </Box>{" "}
                  <Box component="span" sx={{ color: PURPLE_ACCENT }}>business.</Box>
                </Typography>
              )}

              {/* Slide 2: icon grid (4+3 layout, second row centered under first) */}
              {step === 2 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    rowGap: { xs: 2.5, sm: 3 },
                    mx: "auto",
                  }}
                >
                  {[INTRO_GRID_TILES.slice(0, 4), INTRO_GRID_TILES.slice(4)].map(
                    (rowTiles, rowIndex) => (
                      <Box
                        key={`row-${rowIndex}`}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                          columnGap: { xs: 1.5, sm: 2 },
                          flexWrap: "nowrap",
                        }}
                      >
                        {rowTiles.map((tile, idx) => (
                          <BusinessGuideIntroGridCell
                            key={tile.key}
                            Icon={tile.Icon}
                            labelKey={tile.labelKey}
                            labelColor={tile.labelColor}
                            grad={tile.grad}
                            borderGrad={tile.borderGrad}
                            shadow={tile.shadow}
                            cellIndex={rowIndex * 4 + idx}
                            size={INTRO_TILE}
                            t={t}
                          />
                        ))}
                      </Box>
                    )
                  )}
                </Box>
              )}

              {/* Slide 3: let's set you up */}
              {step === 3 && (
                <Typography component="div" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#1a1a1a" }}>
                  {t("businessGuideLets")}{" "}
                  <Box component="span" sx={{ color: YELLOW_ACCENT }}>{t("businessGuideSetYouUp")}</Box>
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* ── Slide 4: setup & earn list ── */}
        {step === 4 && (
          <Fade in timeout={FEATURES_ENTER_MS}>
            <Box sx={{ p: { xs: 3, sm: 6 } }}>
              {/* header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Box sx={{ pr: 2 }}>
                  <Typography component="div" sx={{ fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.2 }}>
                    <Box component="span" sx={{ color: "#1a1a1a" }}>{t("businessGuideSetupEarnPrefix")}{" "}</Box>
                    <Box component="span" sx={{ color: YELLOW_ACCENT }}>{t("businessGuideSetupEarnHighlight")}</Box>
                  </Typography>
                  <Typography sx={{ color: "#777", mt: 0.5, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                    {t("businessGuideSetupEarnBlurb")}
                  </Typography>
                </Box>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={handleClose}
                  sx={{
                    fontSize: "0.8rem",
                    color: "#999",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                    border: 0,
                    background: "none",
                  }}
                >
                  {t("skip")}
                </Link>
              </Box>

              {/* feature rows with vertical timeline */}
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: 17, // center of 36px icon
                    top: 18,
                    bottom: 50,
                    width: 2,
                    bgcolor: "#e8e8e8",
                    borderRadius: 1,
                  }}
                />

                {BUSINESS_GUIDE_FEATURES.map(({ key, Icon, grad, shadow }) => (
                  <Box key={key} sx={{ display: "flex", gap: 1.5, mb: 3, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: FEATURE_ROW_ICON,
                        height: FEATURE_ROW_ICON,
                        minWidth: FEATURE_ROW_ICON,
                        borderRadius: "50%",
                        background: grad,
                        boxShadow: shadow,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        zIndex: 1,
                      }}
                    >
                      <Icon sx={{ color: "#fff", fontSize: FEATURE_ROW_ICON * 0.55 }} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1a1a1a" }}>
                        {t(`businessGuideFeature_${key}_title`)}
                      </Typography>
                      <Typography sx={{ color: "#666", mt: 0.25, lineHeight: 1.45, fontSize: "0.8125rem" }}>
                        {t(`businessGuideFeature_${key}_desc`)}
                      </Typography>
                    </Box>

                    <Link
                      component="button"
                      type="button"
                      onClick={() => openFeatureDetail(key)}
                      aria-label={`${t("userGuideWatchShort")}: ${t(`businessGuideFeature_${key}_title`)}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: PURPLE_ACCENT,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        border: 0,
                        background: "none",
                        p: 0,
                        flexShrink: 0,
                        "&:hover": { color: "#5500b3" },
                      }}
                    >
                      {t("userGuideWatchShort")}
                      <PlayCircleIcon sx={{ fontSize: 16 }} aria-hidden />
                    </Link>
                  </Box>
                ))}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleClose}
                sx={{
                  mt: 1,
                  bgcolor: PURPLE_ACCENT,
                  textTransform: "none",
                  fontWeight: 600,
                  py: 1.25,
                  borderRadius: 2,
                  fontSize: "0.9375rem",
                  "&:hover": { bgcolor: "#5500b3" },
                }}
              >
                {t("businessGuideStartSetup")}
              </Button>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <BusinessGuideFeatureDetailDialog
        featureKey={detailFeatureKey}
        onClose={closeFeatureDetail}
        t={t}
      />
    </Dialog>
  );
};

export default BusinessGuideDialog;
